import Vue from 'vue'
import Vuex from 'vuex'
import { createDeepgramTranscription } from '@/services/deepgramTranscription'

Vue.use(Vuex)

// Single instance for the life of the app
let transcriptionService = null

export default new Vuex.Store({
  state: {
    // Current authentication status: 'unknown' (initial), 'unauthenticated', or 'authenticated'
    //"unauthenticated" | "authenticated" - "unknown" is initial state and then it can transition to "unauthenticated" or "authenticated"
    authState: 'unknown',

    // Real-time transcription data synchronized from the transcription service
    transcription: {
      isRecording: false,      // True if the microphone is actively capturing audio
      isConnecting: false,     // True during the WebSocket connection handshake
      isConnected: false,      // True if the WebSocket is open and ready for data
      finalTranscript: '',     // The accumulated finalized text returned by the provider
      interimTranscript: '',   // Real-time "in-progress" transcript that changes as you speak
      error: null,             // Stores any transcription or connection error messages
      audioLevel: 0,           // Current volume level (0-100) for visual feedback animations
      provider: 'soniox',      // Active STT provider: 'deepgram' | 'soniox'
    },

    // OTP flow state
    otp: null, // { mode, phoneE164, name?, confirmationResult? } | null

    // Signup context
    signupContext: {
      pendingProfile: false
    },

    // User document from Firestore
    userDoc: null,

    // Loading states
    loading: {
      auth: false,
      otpSend: false,
      otpVerify: false,
      userDoc: false
    },

    // Error state
    error: null,

    // In-app notification toast (used for foreground FCM messages)
    toast: null, // { title: string, body: string } | null

    // Transient transcript passed from VoiceAssistant to VoiceResultView without touching the URL
    pendingTranscript: null,

    // Legacy states (keeping for backward compatibility)
    properties: [],
    tasks: []
  },

  mutations: {
    SET_TRANSCRIPTION_STATE(state, transcriptionState) {
      state.transcription = { ...state.transcription, ...transcriptionState }
    },

    SET_PROVIDER(state, provider) {
      state.transcription.provider = provider
    },

    SET_AUTH_STATE(state, authState) {
      state.authState = authState
    },

    SET_OTP_STATE(state, otp) {
      state.otp = otp
    },

    SET_USER_DOC(state, userDoc) {
      state.userDoc = userDoc
    },

    SET_SIGNUP_CONTEXT(state, context) {
      state.signupContext = { ...state.signupContext, ...context }
    },

    SET_LOADING(state, { key, value }) {
      if (key in state.loading) {
        state.loading[key] = value
      }
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    CLEAR_AUTH_STATE(state) {
      state.authState = 'unauthenticated'
      state.otp = null
      state.signupContext = { pendingProfile: false }
      state.userDoc = null
      state.error = null
      state.loading = {
        auth: false,
        otpSend: false,
        otpVerify: false,
        userDoc: false
      }
    },

    // Legacy mutations
    SET_PROPERTIES(state, properties) {
      state.properties = properties
    },

    SET_TASKS(state, tasks) {
      state.tasks = tasks
    },

    ADD_TASK(state, task) {
      state.tasks.unshift(task)
    },

    UPDATE_TASK(state, updatedTask) {
      const index = state.tasks.findIndex(t => t.id === updatedTask.id)
      if (index !== -1) {
        Vue.set(state.tasks, index, updatedTask)
      }
    },

    DELETE_TASK(state, taskId) {
      state.tasks = state.tasks.filter(t => t.id !== taskId)
    },

    SET_TOAST(state, toast) {
      state.toast = toast
    },

    SET_PENDING_TRANSCRIPT(state, transcript) {
      state.pendingTranscript = transcript
    }
  },

  actions: {
    /**
     * Start the actual microphone recording
     */
    async startTranscription({ commit, state }) {
      const provider = state.transcription.provider
      if (!transcriptionService) {
        transcriptionService = createDeepgramTranscription({ provider })
        transcriptionService.setStateChangeCallback((tsState) => {
          commit('SET_TRANSCRIPTION_STATE', tsState)
        })
      }
      return await transcriptionService.startRecording()
    },

    /**
     * Stop the microphone and close the socket connection
     */
    stopTranscription() {
      if (transcriptionService) {
        transcriptionService.stopRecording()
      }
    },

    /**
     * Full cleanup of the connection
     */
    disconnectTranscription() {
      if (transcriptionService) {
        transcriptionService.disconnect()
        transcriptionService = null
      }
    },

    /**
     * Switch STT provider ('deepgram' | 'soniox').
     * Nulls the service singleton so the next startTranscription creates a fresh instance.
     */
    async setProvider({ commit, dispatch }, provider) {
      await dispatch('disconnectTranscription')
      commit('SET_PROVIDER', provider)
    },

    clearTranscripts() {
      if (transcriptionService) {
        transcriptionService.clearTranscripts()
      }
    },

    setAuthState({ commit }, authState) {
      commit('SET_AUTH_STATE', authState)
    },

    setOtpState({ commit }, otp) {
      commit('SET_OTP_STATE', otp)
    },

    setUserDoc({ commit }, userDoc) {
      commit('SET_USER_DOC', userDoc)
    },

    setSignupContext({ commit }, context) {
      commit('SET_SIGNUP_CONTEXT', context)
    },

    setLoading({ commit }, payload) {
      commit('SET_LOADING', payload)
    },

    setError({ commit }, error) {
      commit('SET_ERROR', error)
    },

    clearAuthState({ commit }) {
      commit('CLEAR_AUTH_STATE')
    },

    // Legacy actions
    setProperties({ commit }, properties) {
      commit('SET_PROPERTIES', properties)
    },

    setTasks({ commit }, tasks) {
      commit('SET_TASKS', tasks)
    },

    addTask({ commit }, task) {
      commit('ADD_TASK', task)
    },

    updateTask({ commit }, task) {
      commit('UPDATE_TASK', task)
    },

    deleteTask({ commit }, taskId) {
      commit('DELETE_TASK', taskId)
    },

    setPendingTranscript({ commit }, transcript) {
      commit('SET_PENDING_TRANSCRIPT', transcript)
    },

    showToast({ commit }, { title, body }) {
      commit('SET_TOAST', { title, body })
      setTimeout(() => commit('SET_TOAST', null), 4000)
    }
  },

  getters: {
    isAuthenticated: state => state.authState === 'authenticated',
    transcriptionState: state => state.transcription,
    isLoading: state => Object.values(state.loading).some(v => v),
    userName: state => state.userDoc?.name || '',
    userPhone: state => state.userDoc?.mobile_number || ''
  }
})
